import { useMutation } from "@apollo/client"
import moment from "moment"
import { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"
import { Col, Input, Modal, ModalBody, ModalHeader, Row } from "reactstrap"
import CustomButton from "../layout/CustomButton"
import { ITempBillingPayment, STAMP_TEMP_BILLING_PAYMENT } from "./gql"

// kan-101. Stamping a Recruiter Profit Summary import with the ACH received date
// and a reference id is what opens it for reconciliation. People 2.0 pays by ACH,
// one payment per day, so the reference is conventionally RPYYMMDD — we prefill
// that shape from the date but it stays free text, and the server enforces that
// it is unique.

interface IStampPaymentModalProps {
  isOpen: boolean;
  toggle: () => void;
  payment?: ITempBillingPayment | null;
  refetch: Function;
}

/**
 * 'RP' + the received date as YYMMDD, e.g. 2026-09-15 -> RP260915.
 * receivedDate arrives from <input type="date"> as 'YYYY-MM-DD', so the year is
 * sliced to its last two digits rather than just stripping the dashes.
 */
const referenceFromDate = (receivedDate: string): string => {
  if (!receivedDate) return ''
  const [year, month, day] = receivedDate.split('-')
  if (!year || !month || !day) return ''
  return `RP${year.slice(-2)}${month}${day}`
}

const StampPaymentModal: FC<IStampPaymentModalProps> = ({ isOpen, toggle, payment, refetch }) => {
  const [receivedDate, setReceivedDate] = useState<string>('')
  const [referenceId, setReferenceId] = useState<string>('')
  // Once the executive types their own reference we stop overwriting it when the
  // date changes.
  const [referenceEdited, setReferenceEdited] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [stampPayment, { loading }] = useMutation(STAMP_TEMP_BILLING_PAYMENT, {
    onCompleted: () => {
      toast.success("Payment stamped")
      refetch()
      toggle()
    },
    // The uniqueness conflict arrives as a plain message — show it where the
    // executive is looking, not only as a toast.
    onError: (error) => {
      setErrorMessage(error?.message || "something went wrong")
      toast.error(error?.message || "something went wrong")
    },
  })

  useEffect(() => {
    const existingDate = payment?.paymentReceivedDate
      ? moment(payment?.paymentReceivedDate).format('YYYY-MM-DD')
      : ''
    setReceivedDate(existingDate)
    setReferenceId(payment?.paymentReferenceId || '')
    setReferenceEdited(!!payment?.paymentReferenceId)
    setErrorMessage('')
  }, [isOpen, payment])

  const dateHandler = (value: string) => {
    setReceivedDate(value)
    setErrorMessage('')
    if (!referenceEdited) setReferenceId(referenceFromDate(value))
  }

  const onSave = async () => {
    if (!receivedDate || !referenceId?.trim()) return
    setErrorMessage('')
    await stampPayment({
      variables: {
        input: {
          paymentMetaId: payment?.id,
          paymentReceivedDate: receivedDate,
          paymentReferenceId: referenceId.trim(),
        }
      }
    })
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} className="revampDialog revampDialogWidth">
      <ModalHeader toggle={toggle}>
        {payment?.stamped ? 'Edit Payment Stamp' : 'Stamp Payment'}
      </ModalHeader>
      <ModalBody className="p-4">
        <Col>
          <p className="text-muted">
            {payment?.fileName || '--'}
            {payment?.monthYr ? ` · ${moment(payment?.monthYr).format('MMM-YYYY')}` : ''}
          </p>
          <Row>
            <Col sm="6">
              <p className="font-weight-bold">Payment Received <span className="text-danger">*</span></p>
              <Input
                type="date"
                value={receivedDate}
                max={moment().format('YYYY-MM-DD')}
                onChange={({ target: { value } }) => dateHandler(value)}
              />
            </Col>
            <Col sm="6">
              <p className="font-weight-bold">Payment Reference ID <span className="text-danger">*</span></p>
              <Input
                type="text"
                value={referenceId}
                placeholder="RP260915"
                onChange={({ target: { value } }) => {
                  setReferenceEdited(true)
                  setErrorMessage('')
                  setReferenceId(value)
                }}
              />
              <small className="text-muted">
                Prefilled as RP + the received date (YYMMDD). Must be unique across payments.
              </small>
            </Col>
          </Row>
        </Col>

        {!!errorMessage && (
          <Col className="mt-3">
            <p className="text-danger m-0">{errorMessage}</p>
          </Col>
        )}

        {!!payment?.hasReconciliationRows && (
          <Col className="mt-3">
            <p className="text-warning m-0">
              This payment already has reconciled invoices — its stamp can no longer be changed.
            </p>
          </Col>
        )}

        <div className="mt-5 d-flex w-40 align-self ml-auto">
          <CustomButton
            className="ml-auto"
            disabled={loading || !receivedDate || !referenceId?.trim()}
            loading={loading}
            buttonClick={onSave}
            buttonText={payment?.stamped ? 'Update Stamp' : 'Stamp Payment'}
          />
        </div>
      </ModalBody>
    </Modal>
  )
}

export default StampPaymentModal
